import { Link } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import { ButtonLink } from "./Button";
import { Eyebrow } from "./Chips";
import { CountUp, Reveal, Stagger, StaggerItem } from "@/motion";
import type { ProcessStep, Stat } from "@/types";

/* -------------------------------------------------------------------------- */
/* Layout shells                                                              */
/* -------------------------------------------------------------------------- */

type SectionTone = "default" | "tint" | "surface" | "deep";

interface SectionProps {
  children: ReactNode;
  tone?: SectionTone;
  id?: string;
  wide?: boolean;
  narrow?: boolean;
  flushTop?: boolean;
  flushBottom?: boolean;
  className?: string;
  style?: CSSProperties;
  "aria-labelledby"?: string;
}

const TONE_CLASS: Record<SectionTone, string> = {
  default: "",
  tint: "section--tint",
  surface: "section--surface",
  deep: "section--deep",
};

export function Section({
  children,
  tone = "default",
  id,
  wide,
  narrow,
  flushTop,
  flushBottom,
  className,
  style,
  ...rest
}: SectionProps) {
  const classes = [
    "section",
    TONE_CLASS[tone],
    flushTop ? "section--flush-top" : "",
    flushBottom ? "section--flush-bottom" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const container = ["container", wide ? "container--wide" : "", narrow ? "container--narrow" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={classes} id={id} style={style} {...rest}>
      <div className={container}>{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Section header                                                             */
/* -------------------------------------------------------------------------- */

interface SectionHeadProps {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: "left" | "center";
  action?: ReactNode;
  id?: string;
  level?: 2 | 3;
}

export function SectionHead({
  eyebrow,
  title,
  lead,
  align = "left",
  action,
  id,
  level = 2,
}: SectionHeadProps) {
  const Heading = level === 2 ? "h2" : "h3";

  const body = (
    <>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Heading id={id}>{title}</Heading>
      {lead ? <p className="lead">{lead}</p> : null}
    </>
  );

  if (action) {
    return (
      <Reveal className="section-head section-head--split">
        <div className="section-head__copy">{body}</div>
        {action}
      </Reveal>
    );
  }

  return (
    <Reveal className={`section-head${align === "center" ? " section-head--center" : ""}`}>
      {body}
    </Reveal>
  );
}

/* -------------------------------------------------------------------------- */
/* Breadcrumbs                                                                */
/* -------------------------------------------------------------------------- */

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items, center }: { items: Crumb[]; center?: boolean }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb" style={center ? { justifyContent: "center" } : undefined}>
      <Link to="/">Home</Link>
      {items.map((item, index) => (
        <span key={item.label} style={{ display: "contents" }}>
          <Icon name="chevron-right" />
          {item.to && index < items.length - 1 ? (
            <Link to={item.to}>{item.label}</Link>
          ) : (
            <span aria-current="page">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Stats                                                                      */
/* -------------------------------------------------------------------------- */

export function StatsRow({
  items,
  className,
  decimals = 0,
}: {
  items: Stat[];
  className?: string;
  decimals?: number;
}) {
  return (
    <Stagger className={`stats${className ? ` ${className}` : ""}`} stagger={0.08}>
      {items.map((stat) => (
        <StaggerItem key={stat.label} className="stat">
          <span className="stat__value">
            <CountUp to={stat.value} suffix={stat.suffix} decimals={decimals} />
          </span>
          <span className="stat__label">{stat.label}</span>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

/* -------------------------------------------------------------------------- */
/* Process                                                                    */
/* -------------------------------------------------------------------------- */

export function ProcessTrack({ steps }: { steps: ProcessStep[] }) {
  return (
    <Stagger
      className="process-track"
      stagger={0.07}
      // The step count goes to CSS as a custom property, not as an inline
      // grid-template-columns. An inline declaration beats every media query,
      // so the six-column track survived into a 390px phone at ~60px per
      // column and pushed the whole document sideways.
      style={{ "--steps": steps.length } as React.CSSProperties}
    >
      {steps.map((step, index) => (
        <StaggerItem key={step.title} className="process-step">
          <div className="process-step__num">STEP {String(index + 1).padStart(2, "0")}</div>
          <h3>{step.title}</h3>
          <p>{step.desc}</p>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

/* -------------------------------------------------------------------------- */
/* CTA band                                                                   */
/* -------------------------------------------------------------------------- */

interface CtaBandProps {
  eyebrow: string;
  title: string;
  body: string;
  primary: { label: string; to: string; external?: boolean };
  secondary?: { label: string; to: string; external?: boolean };
}

export function CtaBand({ eyebrow, title, body, primary, secondary }: CtaBandProps) {
  return (
    <Section flushBottom>
      <Reveal preset="scale" className="cta-band">
        <div className="cta-band__inner">
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2>{title}</h2>
            <p>{body}</p>
          </div>
          <div className="cta-band__actions">
            <ButtonLink
              to={primary.to}
              external={primary.external}
              variant="light"
              size="lg"
              icon="arrow-right"
            >
              {primary.label}
            </ButtonLink>
            {secondary ? (
              <ButtonLink
                to={secondary.to}
                external={secondary.external}
                variant="outline-light"
                size="lg"
              >
                {secondary.label}
              </ButtonLink>
            ) : null}
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* Page hero                                                                  */
/* -------------------------------------------------------------------------- */

interface PageHeroProps {
  crumbs: Crumb[];
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  children?: ReactNode;
  media?: ReactNode;
  stats?: Stat[];
}

export function PageHero({ crumbs, eyebrow, title, lead, children, media, stats }: PageHeroProps) {
  return (
    <section className={`page-hero${media ? " page-hero--split" : ""}`}>
      <div className="ambient" aria-hidden="true">
        <span
          className="ambient__blob ambient__blob--green"
          style={{ width: 520, height: 520, top: -240, right: -150, opacity: 0.35 }}
        />
        <span className="ambient__grid" />
      </div>

      <div className="container page-hero__inner">
        {media ? (
          <>
            <div>
              <Breadcrumbs items={crumbs} />
              <Reveal>
                <Eyebrow>{eyebrow}</Eyebrow>
                <h1>{title}</h1>
                {lead ? <p className="lead">{lead}</p> : null}
                {children}
              </Reveal>
            </div>
            <Reveal preset="right" className="page-hero__media">
              {media}
            </Reveal>
          </>
        ) : (
          <>
            <Breadcrumbs items={crumbs} />
            <Reveal className="page-hero__copy">
              <Eyebrow>{eyebrow}</Eyebrow>
              <h1>{title}</h1>
              {lead ? <p className="lead">{lead}</p> : null}
              {children}
            </Reveal>
            {stats ? <StatsRow items={stats} className="page-hero__stats" /> : null}
          </>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */

export function Figure({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <figure>
      <SafeImage src={src} alt={alt} />
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

/**
 * Images are remote (Unsplash in the demo). A failed load gets an architectural
 * fallback rather than a broken-image glyph.
 */
export function SafeImage({
  src,
  alt,
  className,
  loading = "lazy",
}: {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      className={className}
      onError={(event) => event.currentTarget.classList.add("img-failed")}
    />
  );
}
